import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import { classnames } from 'helpers/utils';
import ChevronRight from 'assets/icons/chevron-right.svg';
import * as styles from './pagination.module.scss';

type PaginationProps = {
  numPages: number;
  currentPage: number;
};

const Pagination = ({ numPages, currentPage }: PaginationProps) => {
  const [filteredPages, setFilteredPages] = useState<(number | string)[]>([]);

  const navigate = (newPage: number): string => {
    if (currentPage === 1) {
      return `./${newPage}`;
    }
    if (newPage === 1) {
      return '../';
    }
    return `../${newPage}`;
  };

  useEffect(() => {
    const maxDisplayedPages = 12;

    const generatePagesArray = () => {
      if (numPages <= maxDisplayedPages) {
        return Array.from({ length: numPages }, (_, index) => index + 1);
      }

      const pagesArray = [];

      pagesArray.push(...Array.from({ length: 3 }, (_, index) => index + 1));

      const startRange = Math.max(4, currentPage - 1);
      const endRange = Math.min(numPages - 3, currentPage + 1);

      if (startRange > 4) {
        pagesArray.push('...');
      }

      pagesArray.push(...Array.from({
        length: endRange - startRange + 1,
      }, (_, index) => startRange + index));

      if (endRange < numPages - 3) {
        pagesArray.push('...');
      }

      pagesArray.push(...Array.from({ length: 3 }, (_, index) => numPages - 2 + index));

      return pagesArray;
    };

    const pagesToShow = generatePagesArray();
    setFilteredPages(pagesToShow);
  }, [numPages, currentPage]);

  const checkNavigation = (value: number, thisPage: number) => {
    if (value !== 1) {
      return navigate(value);
    } if (thisPage !== 1) return '../';
    return '';
  };
  return (
    <div className={styles.paginationContainer}>
      {currentPage !== 1 && (
        <Link
          className={styles.cell}
          to={navigate(currentPage - 1)}
          rel="prev"
        >
          <ChevronRight className={classnames(styles.icon, styles.left)} />
        </Link>
      )}
      {filteredPages.map((value, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`pagination-${index}`}>
          {value === '...' ? (
            <span className={styles.ellipsis}>. . .</span>
          ) : (
            <Link
              className={styles.cell}
              to={checkNavigation(Number(value), currentPage)}
              activeClassName={(currentPage === value || (value === 1)) ? styles.selected : ''}
            >
              {value}
            </Link>
          )}
        </React.Fragment>
      ))}

      {currentPage !== numPages && (
        <Link
          className={styles.cell}
          to={navigate(currentPage + 1)}
          rel="next"
        >
          <ChevronRight className={styles.icon} />
        </Link>
      )}
    </div>
  );
};

export { Pagination };
